import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { CloudUploadIcon, Trash2, CheckCircle, Loader2, Eye } from 'lucide-react';
import { useErrorHandler } from '@/hooks/use-error-handler';
import OnboardingWrapper from '@/components/ui/OnboardingWrapper';
import { EXTENDED_STEPS } from '@/utils/onboarding-steps';
import { useUploadOnboardingFile, useUpdateArchitectDocuments, useUpdateSubcontractorDocuments, useExistingOnboardingDocuments, useDeleteFile } from '@/hooks/use-file-upload';
import { FileUploadResponse } from '@/types/api';
import { ExistingDocument } from '@/services/file-upload';

const documentsSchema = z.object({
    workersCompensationPolicy: z.instanceof(File, { message: "Worker's Compensation Policy is required" }),
    generalLiabilityPolicy: z.instanceof(File, { message: "General Liability Policy is required" }),
    businessLicense: z.instanceof(File, { message: "Business License is required" }),
    professionalLicense: z.instanceof(File, { message: "Professional License is required" }),
    companyLogo: z.instanceof(File).optional(),
});

type DocumentsFormData = z.infer<typeof documentsSchema>;

// Document type mapping for API (using the new uploadType values from Swagger)
const DOCUMENT_TYPE_MAPPING: Record<keyof DocumentsFormData, string> = {
    workersCompensationPolicy: 'WORKERS_COMPENSATION_POLICY',
    generalLiabilityPolicy: 'GENERAL_LIABILITY_POLICY',
    businessLicense: 'BUSINESS_LICENSE',
    professionalLicense: 'PROFESSIONAL_LICENSE',
    companyLogo: 'ARCHITECT_LOGO',
};

// Reverse mapping to convert uploadType back to form key
const UPLOAD_TYPE_TO_FORM_KEY: Record<string, keyof DocumentsFormData> = {
    'WORKERS_COMPENSATION_POLICY': 'workersCompensationPolicy',
    'GENERAL_LIABILITY_POLICY': 'generalLiabilityPolicy',
    'BUSINESS_LICENSE': 'businessLicense',
    'PROFESSIONAL_LICENSE': 'professionalLicense',
    'ARCHITECT_LOGO': 'companyLogo',
};

interface DocumentRequirement {
    key: keyof DocumentsFormData;
    title: string;
    description: string;
    required: boolean;
    acceptedTypes: string;
}

const documentRequirements: DocumentRequirement[] = [
    {
        key: 'workersCompensationPolicy',
        title: "Worker's Compensation Policy",
        description: "Current worker's compensation insurance policy",
        required: true,
        acceptedTypes: '.pdf,.jpg,.jpeg,.png',
    },
    {
        key: 'generalLiabilityPolicy',
        title: 'General Liability Policy',
        description: 'Current general liability insurance policy',
        required: true,
        acceptedTypes: '.pdf,.jpg,.jpeg,.png',
    },
    {
        key: 'businessLicense',
        title: 'Business License',
        description: 'Current business license or registration',
        required: true,
        acceptedTypes: '.pdf,.jpg,.jpeg,.png',
    },
    {
        key: 'professionalLicense',
        title: 'Professional License',
        description: 'Professional architecture license',
        required: true,
        acceptedTypes: '.pdf,.jpg,.jpeg,.png',
    },
    {
        key: 'companyLogo',
        title: 'Company Logo',
        description: 'High-resolution company logo for your profile',
        required: false,
        acceptedTypes: '.jpg,.jpeg,.png',
    },
];

export default function DocumentsStep() {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const { handleError } = useErrorHandler();
    const [uploadedFiles, setUploadedFiles] = useState<Record<string, File>>({});
    const [uploadedFileData, setUploadedFileData] = useState<Record<string, FileUploadResponse>>({});
    const [uploadingFiles, setUploadingFiles] = useState<Record<string, boolean>>({});
    const [deletingFiles, setDeletingFiles] = useState<Record<string, boolean>>({});
    const [dragActive, setDragActive] = useState<Record<string, boolean>>({});

    const {
        handleSubmit,
        setValue,
        trigger,
        formState: { isSubmitting, errors },
    } = useForm<DocumentsFormData>({
        resolver: zodResolver(documentsSchema),
        mode: 'onChange',
        defaultValues: {},
    });

    const organizationId = searchParams.get('orgId');
    const accountType = searchParams.get('type') as 'architect' | 'subcontractor';

    // Fetch existing documents
    const { data: existingDocuments, isLoading: isLoadingDocuments } = useExistingOnboardingDocuments(
        organizationId,
        accountType
    );

    // File upload mutation
    const uploadFileMutation = useUploadOnboardingFile(accountType);

    // Document update mutations
    const updateArchitectDocuments = useUpdateArchitectDocuments();
    const updateSubcontractorDocuments = useUpdateSubcontractorDocuments();

    // File delete mutation
    const deleteFileMutation = useDeleteFile(organizationId, accountType);

    // Function to create a File-like object from existing document
    const createFileFromExistingDocument = (doc: ExistingDocument): File => {
        // Create a mock File object for display purposes
        return new File([], doc.originalFileName, {
            type: doc.mimeType,
        }) as any;
    };

    // Effect to load existing documents when they're fetched
    useEffect(() => {
        if (existingDocuments && existingDocuments.length > 0) {
            const newUploadedFiles: Record<string, File> = {};
            const newUploadedFileData: Record<string, FileUploadResponse> = {};

            existingDocuments.forEach(doc => {
                const formKey = UPLOAD_TYPE_TO_FORM_KEY[doc.uploadType];
                if (formKey) {
                    // Create a mock file for display
                    newUploadedFiles[formKey] = createFileFromExistingDocument(doc);

                    // Create FileUploadResponse-like object from existing document
                    newUploadedFileData[formKey] = {
                        id: doc.id, // Include the id for delete operations
                        fileUrl: doc.fileUrl,
                        fileName: doc.fileName,
                        fileSize: doc.fileSize,
                        originalFileName: doc.originalFileName,
                        mimeType: doc.mimeType,
                        // Include signedUrl for secure viewing of existing documents
                        signedUrl: doc.signedUrl,
                        // Add any other properties that FileUploadResponse might have
                    } as FileUploadResponse;

                    // Set form value
                    setValue(formKey, newUploadedFiles[formKey] as any);
                }
            });

            setUploadedFiles(prev => ({ ...prev, ...newUploadedFiles }));
            setUploadedFileData(prev => ({ ...prev, ...newUploadedFileData }));
        }
    }, [existingDocuments, setValue]);

    // Calculate progress - only count successfully uploaded files
    const requiredDocs = documentRequirements.filter(doc => doc.required);
    const uploadedRequiredDocs = requiredDocs.filter(doc => uploadedFileData[doc.key]);
    const progress = uploadedRequiredDocs.length;
    const totalRequired = requiredDocs.length;

    const handleFileUpload = async (key: string, file: File) => {
        // Validate file size (10MB limit)
        const maxSize = 10 * 1024 * 1024; // 10MB in bytes
        if (file.size > maxSize) {
            handleError(new Error('File size must be less than 10MB'), {
                customMessage: 'File size must be less than 10MB',
            });
            return;
        }

        // Store file in local state immediately for UI feedback
        setUploadedFiles(prev => ({ ...prev, [key]: file }));
        setValue(key as keyof DocumentsFormData, file as any);

        // Start uploading file to server
        setUploadingFiles(prev => ({ ...prev, [key]: true }));

        try {
            // Get document requirement for description
            const docRequirement = documentRequirements.find(doc => doc.key === key);

            // Create the new payload structure
            const uploadPayload = {
                file: file,
                uploadType: DOCUMENT_TYPE_MAPPING[key as keyof DocumentsFormData],
                organizationId: organizationId!, // Add organization ID (required)
                description: docRequirement?.description,
                tags: [accountType, 'onboarding', key], // Add relevant tags
                isPublic: false // Set to false for secure documents
            };

            const uploadResponse = await uploadFileMutation.mutateAsync(uploadPayload);

            // Store the upload response data
            setUploadedFileData(prev => ({ ...prev, [key]: uploadResponse }));

            // Clear any validation errors for this field
            trigger(key as keyof DocumentsFormData);

            console.log(`File uploaded successfully for ${key}:`, uploadResponse);
        } catch (error: any) {
            // If upload fails, remove from local state
            setUploadedFiles(prev => {
                const updated = { ...prev };
                delete updated[key];
                return updated;
            });
            setValue(key as keyof DocumentsFormData, undefined as any);

            handleError(error, {
                customMessage: `Failed to upload ${documentRequirements.find(doc => doc.key === key)?.title}. Please try again.`,
            });
        } finally {
            setUploadingFiles(prev => ({ ...prev, [key]: false }));
        }
    };

    const removeFile = async (key: string) => {
        // Check if this is an existing file (has an id) that needs to be deleted from the API
        const fileData = uploadedFileData[key];
        console.log('removeFile called for key:', key, 'fileData:', fileData);

        if (fileData && fileData.id) {
            try {
                console.log('Calling delete API for file ID:', fileData.id);
                setDeletingFiles(prev => ({ ...prev, [key]: true }));
                await deleteFileMutation.mutateAsync(fileData.id);
                console.log('File deleted from API successfully');
            } catch (error) {
                console.error('Failed to delete file from API:', error);
                handleError(error);
                return; // Don't remove from local state if API deletion failed
            } finally {
                setDeletingFiles(prev => ({ ...prev, [key]: false }));
            }
        } else {
            console.log('No API delete needed - file has no ID or is local only');
        }

        // Remove from all state objects
        setUploadedFiles(prev => {
            const updated = { ...prev };
            delete updated[key];
            return updated;
        });
        setUploadedFileData(prev => {
            const updated = { ...prev };
            delete updated[key];
            return updated;
        });
        setDeletingFiles(prev => {
            const updated = { ...prev };
            delete updated[key];
            return updated;
        });
        setValue(key as keyof DocumentsFormData, undefined as any);

        // Trigger validation to show error for required fields
        trigger(key as keyof DocumentsFormData);
    };

    // Drag and drop handlers
    const handleDrag = (e: React.DragEvent, key: string) => {
        e.preventDefault();
        e.stopPropagation();
        if (e.type === 'dragenter' || e.type === 'dragover') {
            setDragActive(prev => ({ ...prev, [key]: true }));
        } else if (e.type === 'dragleave') {
            setDragActive(prev => ({ ...prev, [key]: false }));
        }
    };

    const handleDrop = (e: React.DragEvent, key: string) => {
        e.preventDefault();
        e.stopPropagation();
        setDragActive(prev => ({ ...prev, [key]: false }));

        if (e.dataTransfer.files && e.dataTransfer.files[0]) {
            const file = e.dataTransfer.files[0];
            handleFileUpload(key, file);
        }
    };

    const handleNext = async () => {
        if (!organizationId) {
            return false;
        }

        try {
            // Check if we have any uploaded files with their data
            const documentsToUpdate = Object.keys(uploadedFileData).map(key => {
                const fileData = uploadedFileData[key];
                return {
                    documentType: DOCUMENT_TYPE_MAPPING[key as keyof DocumentsFormData],
                    documentUrl: fileData.fileUrl,
                    fileName: fileData.fileName,
                    fileSize: fileData.fileSize,
                };
            });

            if (documentsToUpdate.length === 0) {
                // No documents to update, just proceed
                console.log('No documents to update, proceeding to next step');
                return true;
            }

            console.log('Updating documents with:', documentsToUpdate);

            // Update documents based on account type
            if (accountType === 'architect') {
                await updateArchitectDocuments.mutateAsync({
                    architectId: organizationId,
                    data: { documents: documentsToUpdate },
                });
            } else {
                await updateSubcontractorDocuments.mutateAsync({
                    subcontractorId: organizationId,
                    data: { documents: documentsToUpdate },
                });
            }

            console.log('Documents updated successfully');
            return true;
        } catch (error: any) {
            handleError(error, {
                customMessage: 'Failed to save documents. Please try again.',
            });
            return false;
        }
    };

    const handleUploadLater = () => {
        if (!organizationId) {
            console.error('Organization ID not found');
            return;
        }

        // Navigate to the next step (agreements)
        navigate(`/onboarding/agreements?orgId=${organizationId}&type=${accountType}`);
    };

    return (
        <OnboardingWrapper
            currentStep={4}
            totalSteps={7}
            steps={EXTENDED_STEPS}
            accountType={accountType}
            onNext={async () => {
                const isValid = await trigger();
                if (!isValid) return false;

                return new Promise((resolve) => {
                    handleSubmit(async () => {
                        const result = await handleNext();
                        resolve(result !== false);
                    })();
                });
            }}
            isLoading={isSubmitting || updateArchitectDocuments.isPending || updateSubcontractorDocuments.isPending || isLoadingDocuments}
            saveButtonText="Save & Continue"
            secondaryButton={{
                text: "Upload Later",
                onClick: handleUploadLater,
                variant: "outline"
            }}
        >
            <div className="space-y-6">
                <div>
                    <h2 className="text-2xl font-bold tracking-tight text-foreground mb-2">
                        Upload Required Documents
                    </h2>
                    <p className="text-muted-foreground">
                        Please upload the following documents. All files should be in PDF, JPG, or PNG format and under 10MB.
                    </p>
                </div>

                {/* Loading State for Documents */}
                {isLoadingDocuments && (
                    <div className="flex items-center justify-center py-8">
                        <div className="flex items-center gap-2">
                            <Loader2 className="h-5 w-5 animate-spin text-blue-600" />
                            <span className="text-muted-foreground">Loading existing documents...</span>
                        </div>
                    </div>
                )}

                {/* Progress Indicator */}
                {!isLoadingDocuments && (
                    <div className="space-y-2">
                        <p className={`text-sm font-medium ${progress === totalRequired ? 'text-green-700' : 'text-foreground'}`}>
                            Progress: {progress} of {totalRequired} required documents uploaded
                        </p>
                        {progress < totalRequired && (
                            <p className="text-sm text-amber-600 font-medium">
                                ⚠️ {totalRequired - progress} documents remaining. You can upload them now or click "Upload Later" to continue and upload them later.
                            </p>
                        )}
                        {progress === totalRequired && (
                            <p className="text-sm text-green-600 font-medium">
                                ✓ All required documents uploaded
                            </p>
                        )}
                    </div>
                )}

                {/* Document Upload Cards */}
                {!isLoadingDocuments && (
                    <div className="space-y-4">
                        {documentRequirements.map((doc) => (
                            <Card key={doc.key} className={`border ${errors[doc.key] && doc.required ? 'border-red-300 bg-red-50' : 'border-border'}`}>
                                <CardContent className="p-0">
                                    {/* Document Header */}
                                    <div className="p-4 border-b border-border/10">
                                        <h3 className="font-medium text-foreground flex items-center gap-2">
                                            {doc.title}
                                            {doc.required && <span className="text-red-500">*</span>}
                                        </h3>
                                        <p className="text-sm text-muted-foreground mt-1">{doc.description}</p>
                                        {errors[doc.key] && (
                                            <p className="text-sm text-red-600 mt-1 font-medium">
                                                {errors[doc.key]?.message}
                                            </p>
                                        )}
                                    </div>

                                    {/* Upload Area */}
                                    <div className="p-6">
                                        {uploadedFiles[doc.key] ? (
                                            <div className={`flex items-center justify-between p-4 rounded-lg ${uploadingFiles[doc.key]
                                                ? 'bg-yellow-50 border border-yellow-200'
                                                : uploadedFileData[doc.key]
                                                    ? 'bg-green-50 border border-green-200'
                                                    : 'bg-blue-50 border border-blue-200'
                                                }`}>
                                                <div className="flex items-center gap-3">
                                                    {uploadingFiles[doc.key] ? (
                                                        <Loader2 className="h-5 w-5 text-yellow-600 animate-spin" />
                                                    ) : uploadedFileData[doc.key] ? (
                                                        <CheckCircle className="h-5 w-5 text-green-600" />
                                                    ) : (
                                                        <CloudUploadIcon className="h-5 w-5 text-blue-600" />
                                                    )}
                                                    <div className="flex flex-col gap-1">
                                                        <span className={`text-sm font-medium ${uploadingFiles[doc.key]
                                                            ? 'text-yellow-700'
                                                            : uploadedFileData[doc.key]
                                                                ? 'text-green-700'
                                                                : 'text-blue-700'
                                                            }`}>
                                                            {uploadedFiles[doc.key].name}
                                                        </span>
                                                        <span className="text-xs text-muted-foreground">
                                                            {uploadingFiles[doc.key]
                                                                ? 'Uploading...'
                                                                : uploadedFileData[doc.key]
                                                                    ? 'Upload complete'
                                                                    : 'Ready to upload'
                                                            }
                                                        </span>
                                                    </div>
                                                </div>
                                                <div className="flex items-center gap-2">
                                                    {/* View Button - Only show if file is successfully uploaded */}
                                                    {uploadedFileData[doc.key] && (uploadedFileData[doc.key].fileUrl || (uploadedFileData[doc.key] as any)?.signedUrl) && (
                                                        <Button
                                                            type="button"
                                                            variant="ghost"
                                                            size="sm"
                                                            onClick={() => {
                                                                // Open file URL in a new tab
                                                                const fileData = uploadedFileData[doc.key];
                                                                // Prefer signedUrl if available (for existing documents), otherwise use fileUrl
                                                                const urlToOpen = (fileData as any)?.signedUrl || fileData?.fileUrl;
                                                                if (urlToOpen) {
                                                                    window.open(urlToOpen, '_blank', 'noopener,noreferrer');
                                                                }
                                                            }}
                                                            className="text-blue-600 hover:text-blue-700"
                                                            title="View uploaded file"
                                                        >
                                                            <Eye className="h-4 w-4" />
                                                        </Button>
                                                    )}

                                                    {/* Delete Button */}
                                                    <Button
                                                        type="button"
                                                        variant="ghost"
                                                        size="sm"
                                                        onClick={() => removeFile(doc.key)}
                                                        className="text-red-600 hover:text-red-700"
                                                        disabled={uploadingFiles[doc.key] || deletingFiles[doc.key]}
                                                        title="Remove file"
                                                    >
                                                        {deletingFiles[doc.key] ? (
                                                            <Loader2 className="h-4 w-4 animate-spin" />
                                                        ) : (
                                                            <Trash2 className="h-4 w-4" />
                                                        )}
                                                    </Button>
                                                </div>
                                            </div>
                                        ) : (
                                            <div
                                                className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${dragActive[doc.key]
                                                    ? 'border-yellow-400 bg-yellow-50'
                                                    : 'border-border hover:border-border'
                                                    }`}
                                                onDragEnter={(e) => handleDrag(e, doc.key)}
                                                onDragLeave={(e) => handleDrag(e, doc.key)}
                                                onDragOver={(e) => handleDrag(e, doc.key)}
                                                onDrop={(e) => handleDrop(e, doc.key)}
                                            >
                                                <CloudUploadIcon className={`h-12 w-12 mx-auto mb-4 ${dragActive[doc.key] ? 'text-yellow-500' : 'text-yellow-400'
                                                    }`} />
                                                <p className="text-base text-muted-foreground mb-2">
                                                    Drag and drop your file here, or click to{' '}
                                                    <Button type="button"
                                                        className="text-yellow-600 font-medium hover:underline"
                                                        onClick={() => document.getElementById(`file-${doc.key}`)?.click()}
                                                    >
                                                        Browse
                                                    </Button>
                                                </p>
                                                <p className="text-sm text-muted-foreground">
                                                    Accepted formats: {doc.acceptedTypes.replace(/\./g, '').toUpperCase()} (max 10MB)
                                                </p>
                                                <Input
                                                    type="file"
                                                    accept={doc.acceptedTypes}
                                                    onChange={(e) => {
                                                        const file = e.target.files?.[0];
                                                        if (file) {
                                                            handleFileUpload(doc.key, file);
                                                            // Reset the input value so the same file can be selected again if needed
                                                            e.target.value = '';
                                                        }
                                                    }}
                                                    className="hidden"
                                                    id={`file-${doc.key}`}
                                                />
                                            </div>
                                        )}
                                    </div>
                                </CardContent>
                            </Card>
                        ))}
                    </div>
                )}

                {/* Info Box */}
                {!isLoadingDocuments && (
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                        <p className="text-sm text-blue-800">
                            <span className="font-medium">Note:</span> All uploaded documents will be securely stored and only used for verification purposes. You can upload additional documents or update existing ones after your account is approved.
                        </p>
                    </div>
                )}
            </div>
        </OnboardingWrapper>
    );
}
